import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch, useAuth } from '../../auth/AuthContext';

const API_BASE = 'https://data.abcz-tr.com';

type Status = 'up' | 'down' | 'checking';

interface ApiKey {
  key_id: string;
  owner_id: string;
  owner_username: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  created_at: string;
}

/** Format an ISO date string into MYT (UTC+8) */
function formatMYT(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kuala_Lumpur',
    }) + ' MYT'
  );
}

/** Fallback copy for non-HTTPS contexts where navigator.clipboard is unavailable. */
function fallbackCopy(text: string, onSuccess: () => void) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
    onSuccess();
  } catch {
    /* ignore */
  }
  document.body.removeChild(ta);
}

function ManageApiTab() {
  const { session } = useAuth();
  const isAdmin = session?.role === 'admin';

  /* ── System status ── */
  const [apiStatus, setApiStatus] = useState<Status>('checking');
  const [dbStatus, setDbStatus] = useState<Status>('checking');

  useEffect(() => {
    const checkHealth = () => {
      fetch(`${API_BASE}/`)
        .then((r) => setApiStatus(r.ok ? 'up' : 'down'))
        .catch(() => setApiStatus('down'));
      fetch(`${API_BASE}/data/health`)
        .then((r) => setDbStatus(r.ok ? 'up' : 'down'))
        .catch(() => setDbStatus('down'));
    };
    checkHealth();
    const interval = setInterval(checkHealth, 15000);
    return () => clearInterval(interval);
  }, []);

  /* ── API keys state ── */
  const [allKeys, setAllKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [revealedKey, setRevealedKey] = useState<{
    raw_key: string;
    name: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');

  const fetchKeys = useCallback(async () => {
    try {
      const r = await apiFetch('/api-keys/list');
      if (r.ok) {
        const data = await r.json();
        setAllKeys(data.keys || []);
      }
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  /* Split keys: own keys vs all keys (admin only) */
  const myKeys = useMemo(
    () => allKeys.filter((k) => k.owner_id === session?.user_id),
    [allKeys, session],
  );
  const otherKeys = useMemo(
    () => allKeys.filter((k) => k.owner_id !== session?.user_id),
    [allKeys, session],
  );
  const filteredAllKeys = useMemo(() => {
    if (!userSearch.trim()) return allKeys;
    const q = userSearch.trim().toLowerCase();
    return allKeys.filter(
      (k) =>
        k.owner_username.toLowerCase().includes(q) ||
        k.name.toLowerCase().includes(q),
    );
  }, [allKeys, userSearch]);

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name) {
      setError('Please enter a name for your API key');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const r = await apiFetch('/api-keys/create', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.detail || 'Failed to create API key');
        return;
      }
      const data = await r.json();
      setRevealedKey({ raw_key: data.raw_key, name: data.name });
      setNewKeyName('');
      fetchKeys();
    } catch {
      setError('Network error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId: string) => {
    setDeletingId(keyId);
    setConfirmRevokeId(null);
    try {
      const r = await apiFetch(`/api-keys/delete?key_id=${keyId}`, {
        method: 'POST',
      });
      if (r.ok) {
        setAllKeys((prev) => prev.filter((k) => k.key_id !== keyId));
      }
    } catch {
      /* ignore */
    } finally {
      setDeletingId(null);
    }
  };

  const copyToClipboard = (text: string) => {
    const onSuccess = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(onSuccess)
        .catch(() => fallbackCopy(text, onSuccess));
    } else {
      fallbackCopy(text, onSuccess);
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 0' }}>
      <style>{`
        @keyframes pulse-green {
          0% { box-shadow: 0 0 0 0 rgba(63, 185, 80, 0.6); }
          70% { box-shadow: 0 0 0 10px rgba(63, 185, 80, 0); }
          100% { box-shadow: 0 0 0 0 rgba(63, 185, 80, 0); }
        }
        @keyframes pulse-red {
          0% { box-shadow: 0 0 0 0 rgba(248, 81, 73, 0.6); }
          70% { box-shadow: 0 0 0 10px rgba(248, 81, 73, 0); }
          100% { box-shadow: 0 0 0 0 rgba(248, 81, 73, 0); }
        }
      `}</style>

      {/* ── Top row: title + status badges ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 32,
        }}
      >
        <h2 style={{ color: '#c9d1d9', fontWeight: 600, margin: 0 }}>
          Manage API
        </h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <StatusBadge label="API" status={apiStatus} />
          <StatusBadge label="Database" status={dbStatus} />
        </div>
      </div>

      {/* ── Create new key ── */}
      <div
        style={{
          background: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 10,
          padding: 24,
          marginBottom: 24,
        }}
      >
        <h3
          style={{
            color: '#c9d1d9',
            margin: '0 0 6px',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          Create API Key
        </h3>
        <p style={{ color: '#8b949e', fontSize: 13, margin: '0 0 16px' }}>
          Generate a key for programmatic access to the data API. The key is
          shown once — copy it immediately.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="text"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="Key name (e.g. research-bot, backtest-script)"
            maxLength={64}
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#0d1117',
              border: '1px solid #30363d',
              borderRadius: 6,
              color: '#c9d1d9',
              fontSize: 14,
              outline: 'none',
            }}
          />
          <button
            onClick={handleCreate}
            disabled={creating}
            style={{
              padding: '8px 20px',
              background: '#238636',
              border: 'none',
              borderRadius: 6,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: creating ? 'not-allowed' : 'pointer',
              opacity: creating ? 0.6 : 1,
            }}
          >
            {creating ? 'Creating...' : 'Generate Key'}
          </button>
        </div>
        {error && (
          <p style={{ color: '#f85149', fontSize: 13, margin: '10px 0 0' }}>
            {error}
          </p>
        )}
      </div>

      {/* ── Revealed key (shown once after creation) ── */}
      {revealedKey && (
        <div
          style={{
            background: '#0d2818',
            border: '1px solid #238636',
            borderRadius: 10,
            padding: 20,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <span style={{ color: '#3fb950', fontWeight: 600, fontSize: 14 }}>
              Key created: {revealedKey.name}
            </span>
            <button
              onClick={() => setRevealedKey(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#8b949e',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              x
            </button>
          </div>
          <p style={{ color: '#adbac7', fontSize: 12, margin: '0 0 10px' }}>
            Copy this key now. You will not be able to see it again.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code
              style={{
                flex: 1,
                padding: '10px 12px',
                background: '#0d1117',
                border: '1px solid #30363d',
                borderRadius: 6,
                color: '#7ee787',
                fontSize: 13,
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {revealedKey.raw_key}
            </code>
            <button
              onClick={() => copyToClipboard(revealedKey.raw_key)}
              style={{
                padding: '10px 16px',
                background: '#21262d',
                border: '1px solid #30363d',
                borderRadius: 6,
                color: copied ? '#3fb950' : '#c9d1d9',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 13,
                whiteSpace: 'nowrap',
              }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {/* ── Your API Keys ── */}
      <KeyTable
        title="Your API Keys"
        keys={myKeys}
        loading={loading}
        showOwner={false}
        emptyText="No API keys yet. Create one above to get started."
        confirmRevokeId={confirmRevokeId}
        deletingId={deletingId}
        onRevoke={(id) => setConfirmRevokeId(id)}
        onConfirmRevoke={handleDelete}
        onCancelRevoke={() => setConfirmRevokeId(null)}
      />

      {/* ── Admin: All User Keys ── */}
      {isAdmin && (
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              background: '#161b22',
              border: '1px solid #30363d',
              borderRadius: 10,
              padding: 24,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  color: '#c9d1d9',
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 600,
                }}
              >
                All User Keys
                {otherKeys.length > 0 && (
                  <span
                    style={{
                      color: '#8b949e',
                      fontWeight: 400,
                      fontSize: 13,
                      marginLeft: 8,
                    }}
                  >
                    ({allKeys.length} total)
                  </span>
                )}
              </h3>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by username or key name..."
                style={{
                  width: 240,
                  padding: '6px 12px',
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: 6,
                  color: '#c9d1d9',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>

            {loading ? (
              <p style={{ color: '#8b949e', fontSize: 14 }}>Loading...</p>
            ) : filteredAllKeys.length === 0 ? (
              <p style={{ color: '#8b949e', fontSize: 14 }}>
                {userSearch.trim()
                  ? 'No keys match your search.'
                  : 'No API keys have been created yet.'}
              </p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #21262d' }}>
                    {['Owner', 'Name', 'Key', 'Created', 'Last Used', ''].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: 'left',
                            padding: '8px 10px',
                            color: '#8b949e',
                            fontSize: 12,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredAllKeys.map((k) => (
                    <tr
                      key={k.key_id}
                      style={{ borderBottom: '1px solid #21262d' }}
                    >
                      <td
                        style={{
                          padding: '12px 10px',
                          color: '#58a6ff',
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {k.owner_username}
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          color: '#c9d1d9',
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {k.name}
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <code
                          style={{
                            color: '#8b949e',
                            fontSize: 13,
                            fontFamily: 'monospace',
                            background: '#0d1117',
                            padding: '3px 8px',
                            borderRadius: 4,
                          }}
                        >
                          {k.key_prefix}...
                        </code>
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          color: '#8b949e',
                          fontSize: 13,
                        }}
                      >
                        {formatMYT(k.created_at)}
                      </td>
                      <td
                        style={{
                          padding: '12px 10px',
                          color: '#8b949e',
                          fontSize: 13,
                        }}
                      >
                        {formatMYT(k.last_used_at)}
                      </td>
                      <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                        {confirmRevokeId === k.key_id ? (
                          <div
                            style={{
                              display: 'flex',
                              gap: 6,
                              justifyContent: 'flex-end',
                              alignItems: 'center',
                            }}
                          >
                            <span
                              style={{
                                color: '#f85149',
                                fontSize: 12,
                                marginRight: 4,
                              }}
                            >
                              Confirm?
                            </span>
                            <button
                              onClick={() => handleDelete(k.key_id)}
                              disabled={deletingId === k.key_id}
                              style={{
                                padding: '5px 12px',
                                background: '#da3633',
                                border: 'none',
                                borderRadius: 6,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor:
                                  deletingId === k.key_id
                                    ? 'not-allowed'
                                    : 'pointer',
                                opacity: deletingId === k.key_id ? 0.5 : 1,
                              }}
                            >
                              {deletingId === k.key_id ? '...' : 'Yes'}
                            </button>
                            <button
                              onClick={() => setConfirmRevokeId(null)}
                              style={{
                                padding: '5px 12px',
                                background: 'transparent',
                                border: '1px solid #30363d',
                                borderRadius: 6,
                                color: '#8b949e',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmRevokeId(k.key_id)}
                            style={{
                              padding: '5px 14px',
                              background: 'transparent',
                              border: '1px solid #f85149',
                              borderRadius: 6,
                              color: '#f85149',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <p
              style={{
                color: '#484f58',
                fontSize: 12,
                marginTop: 16,
                marginBottom: 0,
              }}
            >
              Showing all API keys across all users. Revoking a key stops it
              immediately.
            </p>
          </div>
        </div>
      )}

      {/* ── Usage guide ── */}
      <div
        style={{
          background: '#161b22',
          border: '1px solid #30363d',
          borderRadius: 10,
          padding: 24,
          marginTop: 24,
        }}
      >
        <h3
          style={{
            color: '#c9d1d9',
            margin: '0 0 12px',
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          Quick Start
        </h3>
        <p style={{ color: '#8b949e', fontSize: 13, margin: '0 0 12px' }}>
          Use your API key with the{' '}
          <code style={{ color: '#79c0ff' }}>X-API-Key</code> header to fetch
          data from the ClickHouse database as JSON or download as Parquet ZIP
          files.
        </p>
        <div
          style={{
            background: '#0d1117',
            borderRadius: 8,
            padding: 16,
            fontFamily: 'monospace',
            fontSize: 13,
            color: '#c9d1d9',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >
          <div style={{ color: '#8b949e' }}># Fetch JSON data</div>
          <div>
            <span style={{ color: '#ff7b72' }}>curl</span>{' '}
            <span style={{ color: '#a5d6ff' }}>-H</span>{' '}
            <span style={{ color: '#a5d6ff' }}>
              "X-API-Key: dak_your_key_here"
            </span>{' '}
            <span style={{ color: '#79c0ff' }}>\</span>
          </div>
          <div style={{ paddingLeft: 24 }}>
            <span style={{ color: '#a5d6ff' }}>
              "{API_BASE}
              /data?coins=BTC,ETH&amp;timeframes=1h&amp;data_types=spot_ohlcv&amp;data_source=cleaned&amp;date_from=2025-01-01&amp;date_to=2025-01-02"
            </span>
          </div>
          <br />
          <div style={{ color: '#8b949e' }}># Download Parquet ZIP</div>
          <div>
            <span style={{ color: '#ff7b72' }}>curl</span>{' '}
            <span style={{ color: '#a5d6ff' }}>-H</span>{' '}
            <span style={{ color: '#a5d6ff' }}>
              "X-API-Key: dak_your_key_here"
            </span>{' '}
            <span style={{ color: '#79c0ff' }}>\</span>
          </div>
          <div style={{ paddingLeft: 24 }}>
            <span style={{ color: '#a5d6ff' }}>-X POST -d</span>{' '}
            <span style={{ color: '#a5d6ff' }}>
              {`'{"coins":["BTC"],"timeframes":["1h"],"data_types":["spot_ohlcv"],"data_source":"cleaned","date_from":"2025-01-01","date_to":"2025-01-02"}'`}
            </span>{' '}
            <span style={{ color: '#79c0ff' }}>\</span>
          </div>
          <div style={{ paddingLeft: 24 }}>
            <span style={{ color: '#a5d6ff' }}>
              -H "Content-Type: application/json"
            </span>{' '}
            <span style={{ color: '#a5d6ff' }}>{API_BASE}/data/parquet</span>{' '}
            <span style={{ color: '#a5d6ff' }}>-o data.zip</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Reusable key table (for "Your API Keys" section) ── */
function KeyTable({
  title,
  keys,
  loading,
  showOwner,
  emptyText,
  confirmRevokeId,
  deletingId,
  onRevoke,
  onConfirmRevoke,
  onCancelRevoke,
}: {
  title: string;
  keys: ApiKey[];
  loading: boolean;
  showOwner: boolean;
  emptyText: string;
  confirmRevokeId: string | null;
  deletingId: string | null;
  onRevoke: (id: string) => void;
  onConfirmRevoke: (id: string) => void;
  onCancelRevoke: () => void;
}) {
  const headers = showOwner
    ? ['Owner', 'Name', 'Key', 'Created', 'Last Used', '']
    : ['Name', 'Key', 'Created', 'Last Used', ''];

  return (
    <div
      style={{
        background: '#161b22',
        border: '1px solid #30363d',
        borderRadius: 10,
        padding: 24,
      }}
    >
      <h3
        style={{
          color: '#c9d1d9',
          margin: '0 0 16px',
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {title}
      </h3>

      {loading ? (
        <p style={{ color: '#8b949e', fontSize: 14 }}>Loading...</p>
      ) : keys.length === 0 ? (
        <p style={{ color: '#8b949e', fontSize: 14 }}>{emptyText}</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #21262d' }}>
              {headers.map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    color: '#8b949e',
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.key_id} style={{ borderBottom: '1px solid #21262d' }}>
                {showOwner && (
                  <td
                    style={{
                      padding: '12px 10px',
                      color: '#58a6ff',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {k.owner_username}
                  </td>
                )}
                <td
                  style={{
                    padding: '12px 10px',
                    color: '#c9d1d9',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {k.name}
                </td>
                <td style={{ padding: '12px 10px' }}>
                  <code
                    style={{
                      color: '#8b949e',
                      fontSize: 13,
                      fontFamily: 'monospace',
                      background: '#0d1117',
                      padding: '3px 8px',
                      borderRadius: 4,
                    }}
                  >
                    {k.key_prefix}...
                  </code>
                </td>
                <td
                  style={{
                    padding: '12px 10px',
                    color: '#8b949e',
                    fontSize: 13,
                  }}
                >
                  {formatMYT(k.created_at)}
                </td>
                <td
                  style={{
                    padding: '12px 10px',
                    color: '#8b949e',
                    fontSize: 13,
                  }}
                >
                  {formatMYT(k.last_used_at)}
                </td>
                <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                  {confirmRevokeId === k.key_id ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          color: '#f85149',
                          fontSize: 12,
                          marginRight: 4,
                        }}
                      >
                        Confirm?
                      </span>
                      <button
                        onClick={() => onConfirmRevoke(k.key_id)}
                        disabled={deletingId === k.key_id}
                        style={{
                          padding: '5px 12px',
                          background: '#da3633',
                          border: 'none',
                          borderRadius: 6,
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor:
                            deletingId === k.key_id ? 'not-allowed' : 'pointer',
                          opacity: deletingId === k.key_id ? 0.5 : 1,
                        }}
                      >
                        {deletingId === k.key_id ? '...' : 'Yes'}
                      </button>
                      <button
                        onClick={onCancelRevoke}
                        style={{
                          padding: '5px 12px',
                          background: 'transparent',
                          border: '1px solid #30363d',
                          borderRadius: 6,
                          color: '#8b949e',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => onRevoke(k.key_id)}
                      style={{
                        padding: '5px 14px',
                        background: 'transparent',
                        border: '1px solid #f85149',
                        borderRadius: 6,
                        color: '#f85149',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p
        style={{
          color: '#484f58',
          fontSize: 12,
          marginTop: 16,
          marginBottom: 0,
        }}
      >
        Maximum 5 keys per user. Revoked keys stop working immediately.
      </p>
    </div>
  );
}

/* ── Compact status badge (top-right) ── */
function StatusBadge({ label, status }: { label: string; status: Status }) {
  const isUp = status === 'up';
  const checking = status === 'checking';
  const color = checking ? '#8b949e' : isUp ? '#3fb950' : '#f85149';
  const animation = checking
    ? 'none'
    : isUp
      ? 'pulse-green 2s infinite'
      : 'pulse-red 2s infinite';
  const text = checking ? '...' : isUp ? 'Connected' : 'Down';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: '#0d1117',
        border: '1px solid #30363d',
        borderRadius: 20,
        padding: '6px 14px',
      }}
    >
      <span style={{ color: '#8b949e', fontSize: 12, fontWeight: 600 }}>
        {label}
      </span>
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          animation,
        }}
      />
      <span style={{ color, fontSize: 11, fontFamily: 'monospace' }}>
        {text}
      </span>
    </div>
  );
}

export default ManageApiTab;
